import React, { createContext, useCallback, useContext, useRef } from 'react';

// ─── Contexto ─────────────────────────────────────────────────────────────────
//
// Cada PageRender envolve seus filhos com ZIndexProvider.
// Assim o z-index é local à página — não há risco de vazar para a TaskBar
// ou para outras páginas, já que cada uma vive em seu próprio translateX.

interface ZIndexContextType {
  /** Retorna o próximo z-index disponível dentro desta página */
  next: () => number;
  /** Libera o z-index ao fechar (decrementa o contador) */
  release: () => void;
  /** Valor base do próximo modal (sem chamar next) */
  peek: () => number;
}

// BASE_Z precisa ser maior que qualquer z-index de layout interno da página.
// Referência: sticky headers da Grade usam z=2, resize handle z=3.
// 100 dá folga suficiente para tooltips, dropdowns e outros elementos de layout.
const BASE_Z = 100;
const STEP   = 100; // espaço entre camadas — permite elementos internos ao modal (tooltip, select) sem conflito

const ZIndexContext = createContext<ZIndexContextType>({
  next:    () => BASE_Z,
  release: () => {},
  peek:    () => BASE_Z,
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export const ZIndexProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const counter = useRef(0); // quantas camadas estão abertas

  const next = useCallback((): number => {
    counter.current += 1;
    return BASE_Z + counter.current * STEP;
  }, []);

  const release = useCallback(() => {
    counter.current = Math.max(0, counter.current - 1);
  }, []);

  const peek = useCallback((): number => {
    return BASE_Z + (counter.current + 1) * STEP;
  }, []);

  return (
    <ZIndexContext.Provider value={{ next, release, peek }}>
      {children}
    </ZIndexContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useZIndex = () => useContext(ZIndexContext);